/**
 * MDP (Model Data Platform) management for CDP SDK.
 */

import { CDPSession, getSession } from '../session';

export class MDPManager {
  constructor(private readonly session: CDPSession = getSession()) {}

  async listModels(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/mdp/mdp_model/list', { params });
  }

  async getColumns(params?: Record<string, any>): Promise<Record<string, any>> {
    return this.session.get('/api/mdp/mdp_model/getColumns', { params });
  }

  // --- Table views (api/mdp/mdt/views/*) ---

  async getViews(tableId: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdt/views/list', {
      data: { tableId, type: 2 },
    });
    return res?.data ?? [];
  }

  async getViewsByProjectUuid(projectUuid: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdt/views/list', {
      data: { projectUuid },
    });
    return res?.data ?? [];
  }

  async getView(uuid: string): Promise<Record<string, any>> {
    const res = await this.session.get(`/api/mdp/mdt/views/${uuid}`);
    return res?.data ?? {};
  }

  async saveView(tableId: string, view: Record<string, any>): Promise<Record<string, any>> {
    const { id, ...rest } = view;
    const content = JSON.stringify(rest);
    return this.session.post('/api/mdp/mdt/views/save', {
      data: { tableId, id, content, name: view.name },
    });
  }

  async addView(tableId: string, view: Record<string, any>): Promise<Record<string, any>> {
    const { id, ...rest } = view;
    const content = JSON.stringify(rest);
    return this.session.post('/api/mdp/mdt/views/save', {
      data: { tableId, content, name: view.name, type: 2 },
    });
  }

  async deleteView(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/mdp/mdt/views/delete/${uuid}`);
  }

  // --- Tables (api/mdp/mdt/*) ---

  async getTables(projectUuid: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdt/list', {
      data: { projectUuid },
    });
    return res?.data ?? [];
  }

  async getTable(uuid: string): Promise<Record<string, any>> {
    const res = await this.session.get(`/api/mdp/mdt/get/${uuid}`);
    return res?.data ?? {};
  }

  async saveTable(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdt/save', { data });
  }

  async deleteTable(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/mdp/mdt/delete/${uuid}`);
  }

  async copyTable(originId: string, name: string, label: string): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdt/copy', {
      data: { originId, name, label },
    });
  }

  async batchCopyTables(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdt/batchCopy', { data });
  }

  async instantiateTable(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/mdp/mdt/instantiateTable/${uuid}`);
  }

  // --- Table catalogs (api/mdp/mdt/catalogs/*) ---

  async getCatalogs(projectUuid: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdt/catalogs/list', {
      data: { projectUuid },
    });
    return res?.data ?? [];
  }

  async saveCatalog(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdt/catalogs/save', { data });
  }

  async deleteCatalog(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/mdp/mdt/catalogs/delete/${uuid}`);
  }

  // --- Enum types (api/mdp/mdt/enumtypes/*) ---

  async getEnumTypes(projectUuid: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdt/enumtypes/list', {
      data: { projectUuid },
    });
    return res?.data ?? [];
  }

  async getEnumType(uuid: string): Promise<Record<string, any>> {
    const res = await this.session.get(`/api/mdp/mdt/enumtypes/${uuid}`);
    return res?.data ?? {};
  }

  async getEnumTypeByCode(code: string): Promise<Record<string, any> | null> {
    const res = await this.session.post('/api/mdp/mdt/enumtypes/list', {
      data: { codes: [code] },
    });
    return res?.data?.[0] ?? null;
  }

  async saveEnumType(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdt/enumtypes/save', { data });
  }

  async deleteEnumType(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/mdp/mdt/enumtypes/delete/${uuid}`);
  }

  // --- Table data (api/mdp/mdd/*) ---

  async getTableDatas(data: Record<string, any>): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/mdp/mdd/list', {
      data: { ...data, page: data.page ?? { size: 1000 } },
    });
    return res?.data?.datas ?? [];
  }

  async getTableFirstData(data: Record<string, any>): Promise<Record<string, any> | undefined> {
    const res = await this.session.post('/api/mdp/mdd/list', {
      data: { ...data, page: { ...data.page, size: 1 } },
    });
    return res?.data?.datas?.[0];
  }

  async createTableDatas(
    tableId: string,
    datas: Record<string, any>[],
  ): Promise<Record<string, any>> {
    const batchSize = 450;
    let lastRes: Record<string, any> = {};
    for (let i = 0; i < datas.length; i += batchSize) {
      const batchData = datas.slice(i, i + batchSize);
      const res = await this.session.post('/api/mdp/mdd/create', {
        data: { tableId, datas: batchData },
      });
      lastRes = res;
      if (!res?.data) break;
    }
    return lastRes;
  }

  async deleteTableDatas(tableId: string, ids: string[]): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdd/delete', {
      data: { tableId, ids },
    });
  }

  async updateTableDatas(
    tableId: string,
    datas: Record<string, any>[],
  ): Promise<Record<string, any>> {
    return this.session.post('/api/mdp/mdd/update', {
      data: { tableId, datas },
    });
  }

  // --- Table cron jobs (api/cdp/cdp_cron_job/extend/*) ---

  async getCronJobs(projectUuid: string, tableId: string): Promise<Record<string, any>[]> {
    const res = await this.session.post('/api/cdp/cdp_cron_job/extend/list', {
      data: { projectUuid, jobType: 1, resourceType: 1, resourceUuid: tableId },
    });
    return res?.data ?? [];
  }

  async saveCronJob(data: Record<string, any>): Promise<Record<string, any>> {
    return this.session.post('/api/cdp/cdp_cron_job/extend/save', { data });
  }

  async deleteCronJob(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_cron_job/extend/delete/${uuid}`);
  }

  async deployCronJob(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_cron_job/extend/deploy/${uuid}`);
  }

  async cancelCronJobDeploy(uuid: string): Promise<Record<string, any>> {
    return this.session.post(`/api/cdp/cdp_cron_job/extend/cancel/${uuid}`);
  }

  async getCronScheduleTime(): Promise<Record<string, any>> {
    return this.session.get('/api/cdp/cdp_cron_job/extend/getScheduleTime');
  }
}
